import { Slot, Slottable } from '@radix-ui/react-slot';
import { CheckCircle } from '@timelyapp/icons/solid-icons';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import classNames from 'classnames';
import React from 'react';
import type { BoxProps } from '../box/Box';
import { Box } from '../box/Box';
import styles from './Card.module.scss';

const cvaCard = cva(styles.container, {
  variants: {
    // variant: {
    //   default: styles['variant-default'],
    // },
    interactive: {
      true: styles.interactive,
    },
    selected: {
      true: [styles.interactive, styles.selected],
    },
    disabled: {
      true: styles.disabled,
    },
    size: {
      medium: styles['size-medium'],
      small: styles['size-small'],
    },
  },
  defaultVariants: { size: 'medium', interactive: false, selected: false },
});

type CardContainerProps = Omit<BoxProps, 'as'> &
  VariantProps<typeof cvaCard> & { asChild?: boolean; fancyCheck?: boolean };

export const Container = React.forwardRef<HTMLDivElement, CardContainerProps>((props, ref) => {
  const {
    asChild,
    children,
    className,
    // Make interactive default to true if variant is 'asRadioOption'
    interactive: _interactive,
    selected,
    disabled,
    size = 'medium',
    fancyCheck = false,
    ...rest
  } = props;
  const Component = asChild ? Slot : Box.Column;
  const interactive = _interactive && !disabled ? true : false;
  return (
    <Component
      ref={ref}
      {...rest}
      className={cvaCard({ selected, interactive, size, disabled, className })}
    >
      {!!fancyCheck && <CheckCircle className={styles.selectedIndicator} />}

      <Slottable>{children}</Slottable>
    </Component>
  );
});

type FormRadioContainerProps = Omit<React.HTMLProps<HTMLInputElement>, 'size'> & CardContainerProps;
const FormRadioContainer = React.forwardRef<HTMLInputElement, FormRadioContainerProps>(
  (props, ref) => {
    const {
      children,
      selected,
      disabled = false,
      interactive: interactiveProp,
      size,
      fancyCheck = false,
      className,
      ...rest
    } = props;
    const id = props.id || `card-radio-${props.name}-${props.value}`;

    const checked = rest.checked || selected;
    return (
      <Container
        size={size}
        interactive
        selected={checked}
        fancyCheck={fancyCheck}
        disabled={disabled}
        asChild
      >
        <label data-unset-style htmlFor={id} style={{ color: 'inherit' }}>
          {children}
          <input
            ref={ref}
            id={id}
            type={rest.type || 'radio'}
            checked={checked}
            style={{ position: 'absolute', width: 0, height: 0, opacity: 0 }}
            disabled={disabled}
            {...rest}
          />
        </label>
      </Container>
    );
  }
);

type HeaderProps = React.HTMLAttributes<HTMLDivElement> & BoxProps & { asChild?: boolean };
export const Header = React.forwardRef<HTMLDivElement, HeaderProps>((props, ref) => {
  const { asChild, className, ...rest } = props;
  const Component = asChild ? Slot : Box.Row;
  return (
    <Component ref={ref} gap="large" {...rest} className={classNames(styles.header, className)} />
  );
});

type CoverImageProps = React.HTMLAttributes<HTMLDivElement> & BoxProps & { asChild?: boolean };
export const CoverImage = React.forwardRef<HTMLDivElement, CoverImageProps>((props, ref) => {
  const { asChild, className, ...rest } = props;
  const Component = asChild ? Slot : 'div';
  return (
    <Component
      ref={ref}
      gap="large"
      {...rest}
      className={classNames(styles.coverImage, className)}
    />
  );
});

type BodyProps = React.HTMLAttributes<HTMLDivElement> & BoxProps & { asChild?: boolean };
export const Body = React.forwardRef<HTMLDivElement, BodyProps>((props, ref) => {
  const { asChild, className, ...rest } = props;
  const Component = asChild ? Slot : Box.Row;
  return <Component ref={ref} {...rest} className={classNames(styles.body, className)} />;
});

/**
 * Re-visit the stuff below
 */
export type PropertyItem = {
  prefix?: string | React.ReactNode;
  label?: string;
  postfix?: string | React.ReactNode;
  hidden?: boolean;
};

/**
 * re-visit this, should be dealt with differently
 */
type PropertyListProps = React.HTMLAttributes<HTMLDivElement> & { asChild?: boolean };
export const PropertyList = React.forwardRef<HTMLDivElement, PropertyListProps>((props, ref) => {
  const { asChild, children, className, ...rest } = props;
  const Component = asChild ? Slot : 'div';
  return (
    <Component ref={ref} {...rest} className={classNames(className)}>
      <Box.Row gap="medium" justifyContent="middle" className={styles.propertyList}>
        {children}
      </Box.Row>
    </Component>
  );
});

type VisibleOnInteractionProps = React.HTMLAttributes<HTMLSpanElement> & { asChild?: boolean };
export const VisibleOnInteraction = React.forwardRef<HTMLSpanElement, VisibleOnInteractionProps>(
  (props, ref) => {
    const { asChild, className, ...rest } = props;
    const Component = asChild ? Slot : 'span';
    return (
      <Component
        ref={ref}
        {...rest}
        className={classNames(styles.visibleOnInteraction, className)}
      />
    );
  }
);

export const Card = Object.assign(Container, {
  Container, // exported for legacy reasons, remove in future
  CoverImage,
  Header,
  Body,
  PropertyList,
  VisibleOnInteraction,
  Form: {
    Radio: FormRadioContainer,
  },
});
